import { useCallback } from 'react';
import { createSelector } from 'reselect';

import { ComponentStates } from '../../enums/component-states';
import { useFulfillmentPlannerContext } from '../../fulfillment-planner/context';
import { useFulfillmentRestrictionsByFPId } from '../../hooks/entities/selectors/fulfillment-plans';

const memoizeCustoms = createSelector(
  (content) => content,
  (_, nonDelivery) => nonDelivery,
  (_, __, customsItems) => customsItems,
  (_, __, ___, exemptionCodeOrITN) => exemptionCodeOrITN,
  (_, __, ___, ____, dutiesPaid) => dutiesPaid,
  (_, __, ___, ____, _____, postagePaid) => postagePaid,
  (_, __, ___, ____, _____, ______, exportDeclarationNumber) => exportDeclarationNumber,
  (_, __, ___, ____, _____, ______, _______, invoiceNumber) => invoiceNumber,
  (_, __, ___, ____, _____, ______, _______, ________, importerOfRecord) => importerOfRecord,
  (
    content,
    nonDelivery,
    customsItems,
    exemptionCodeOrITN,
    dutiesPaid,
    postagePaid,
    exportDeclarationNumber,
    invoiceNumber,
    importerOfRecord,
  ) => ({
    content,
    nonDelivery,
    customsItems,
    exemptionCodeOrITN,
    dutiesPaid,
    postagePaid,
    exportDeclarationNumber,
    invoiceNumber,
    importerOfRecord,
  }),
);

export const useFulfillmentPlannerCustoms = (fulfillmentPlanId) => {
  const { customs, needsCustoms: needsCustomsValue, onCustomsChange } = useFulfillmentPlannerContext();
  // Customs can be a boolean when it is included in fulfillment and an object when calling api/customs
  const needsCustoms = typeof needsCustomsValue === 'boolean' ? needsCustomsValue : needsCustomsValue?.needsCustoms;
  const {
    content,
    nonDelivery,
    customsItems,
    exemptionCodeOrITN,
    dutiesPaid,
    postagePaid,
    exportDeclarationNumber,
    invoiceNumber,
    importerOfRecord,
  } = customs;
  const memoizedCustoms = memoizeCustoms(
    content,
    nonDelivery,
    customsItems,
    exemptionCodeOrITN,
    dutiesPaid,
    postagePaid,
    exportDeclarationNumber,
    invoiceNumber,
    importerOfRecord,
  );
  const uiRestrictions = useFulfillmentRestrictionsByFPId(fulfillmentPlanId);

  const hideCustomsDeclarations = uiRestrictions?.labelConfiguration?.customs?.componentState === ComponentStates.Hidden;

  const onSave = useCallback(
    (updatedCustoms) => {
      onCustomsChange(updatedCustoms, needsCustoms);
    },
    [onCustomsChange, needsCustoms],
  );

  return {
    customs: memoizedCustoms,
    needsCustoms,
    onSave,
    hideCustomsDeclarations,
  };
};
